import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export function useRoutineTasks(itemId = null) {
    const [routineTasks, setRoutineTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchTasks = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            let query = supabase
                .from('routine_tasks')
                .select('*')
                .eq('user_id', user.id)
                .order('completed_at', { ascending: false });

            if (itemId) {
                // If we pass itemId, filter by it. Since routine_tasks has item_id
                query = query.eq('item_id', itemId);
            }

            const { data, error } = await query;
            if (error) throw error;
            setRoutineTasks(data || []);
        } catch (error) {
            console.error('Error fetching routine tasks:', error);
        } finally {
            setLoading(false);
        }
    }, [itemId]);

    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchTasks();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchTasks]);

    const addRoutineTask = async (taskData) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return null;

            const { data, error } = await supabase
                .from('routine_tasks')
                .insert([{
                    user_id: user.id,
                    ...taskData
                }])
                .select()
                .single();

            if (error) throw error;
            setRoutineTasks(prev => [data, ...prev]);
            return data;
        } catch (error) {
            console.error('Error adding routine task:', error);
            return null;
        }
    };

    const deleteRoutineTask = async (taskId) => {
        try {
            const { error } = await supabase
                .from('routine_tasks')
                .delete()
                .eq('id', taskId);

            if (error) throw error;
            setRoutineTasks(prev => prev.filter(t => t.id !== taskId));
            return true;
        } catch (error) {
            console.error('Error deleting routine task:', error);
            return false;
        }
    };

    return {
        routineTasks,
        loading,
        refetch: fetchTasks,
        addRoutineTask,
        deleteRoutineTask
    };
}
